import axios from "axios";
import { handleExpiredAuthSession } from "../utils/authSessionCleanup";
import { maybeEmitPermissionDeniedFromApiError } from "../utils/permissionDeniedEvents";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://127.0.0.1:8000";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Generate a RFC 4122 v4 UUID for request idempotency keys.
// Uses the native Web Crypto API available in all modern browsers and Node 14.17+.
// Falls back to a Math.random()-based implementation for older environments.
function generateUUID() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Add auth token and idempotency key to every outgoing request.
// A fresh UUID is generated per request call, so:
//   - Genuine sequential submissions each get their own key → all allowed.
//   - Network retries / proxy re-sends carry the exact same Axios config
//     object and therefore the same key → blocked by the backend middleware.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Attach a unique idempotency key to every POST request so the backend
  // middleware can detect and reject duplicate network-level submissions.
  if (config?.method?.toLowerCase() === 'post') {
    config.headers['X-Idempotency-Key'] = generateUUID();
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = Number(error?.response?.status || 0);
    const detail = error?.response?.data?.detail;
    const detailCode = typeof detail === 'object' ? String(detail?.code || '').toUpperCase() : '';
    if (status === 401) {
      handleExpiredAuthSession(detailCode || null);
    }
    maybeEmitPermissionDeniedFromApiError(error);
    return Promise.reject(error);
  },
);

export default api;
